import { useMemo, useRef } from "react";

import { useQuery } from "@tanstack/react-query";
import { addMinutes, formatISO, isAfter, subMinutes } from "date-fns";

import { useHistoryDecisionsV2 } from "src/api/decisionHistoryV2/decisionHistoryQueries";
import { ExporterDatasetsEndpoint } from "src/api/endpoints";
import { NODE_EXECUTIONS_POLLING_INTERVAL } from "src/api/testRuns/testRunQueries";
import { isJobRunCompleted } from "src/flowContainer/RunFlowButton/utils";
import { JobRun } from "src/jobs/types";
import { computeEffectiveHasNextDecisionsPage } from "src/nodeEditor/JobRunIntermediateResultTableWrapper/JobRunIntermediateResultTableWrapper";
import {
  CustomStatusFilterKey,
  toDecisionStatusFilter,
} from "src/nodeEditor/JobRunIntermediateResultTableWrapper/utils";
import { useAuthoringContext } from "src/router/routerContextHooks";

export const MAX_LTR_ROW_COUNT = 500;

export const useDataset = (datasetId: string) => {
  const { workspace } = useAuthoringContext();
  return useQuery({
    queryKey: ["minimal_dataset", datasetId],
    queryFn: async () => {
      const response = await ExporterDatasetsEndpoint.getDataset(
        workspace.base_url ?? "",
        datasetId,
        { from: 0, size: MAX_LTR_ROW_COUNT },
      );
      return response;
    },
  });
};

export const useOutputNodeDecisions = ({
  jobRun,
  enabled,
  timeFilter,
  statusFilter,
  expectedResultsCount,
  shouldPoll,
}: {
  jobRun: JobRun;
  enabled: boolean;
  timeFilter: { fromStartTime: string; untilStartTime: string };
  statusFilter: CustomStatusFilterKey;
  expectedResultsCount: number;
  shouldPoll: boolean;
}) => {
  const { workspace, flow, version } = useAuthoringContext();
  const {
    data: decisions,
    isFetching: isFetchingDecisions,
    fetchNextPage: fetchNextDecisionsPage,
    isFetchingNextPage: isFetchingNextDecisionsPage,
    hasNextPage: hasNextDecisionsPage,
  } = useHistoryDecisionsV2({
    baseUrl: workspace.base_url ?? "",
    include_response_data: true,
    include_node_results: true,
    include_execution_metadata: true,
    filters: {
      order: "asc",
      fields: {
        flow_version_id: version.id,
        job_id: jobRun.job_id,
        job_run_id: jobRun.id,
        status: toDecisionStatusFilter(statusFilter),
      },
      timeWindow: {
        from: new Date(timeFilter.fromStartTime),
        to: new Date(timeFilter.untilStartTime),
      },
    },
    flowSlug: flow.slug,
    enabled,
    icebergOverride: true,
    refetchInterval: () =>
      shouldPoll ? NODE_EXECUTIONS_POLLING_INTERVAL : false,
  });

  const loadedCount =
    decisions?.pages.reduce((acc, page) => acc + page.data.length, 0) ?? 0;

  const effectiveHasNextDecisionsPage = computeEffectiveHasNextDecisionsPage({
    hasNextDecisionsPage,
    expectedResultsCount,
    loadedCount,
  });

  return {
    decisions,
    isFetchingDecisions,
    isFetchingNextDecisionsPage,
    hasNextDecisionsPage: effectiveHasNextDecisionsPage,
    isPollingDecisions: shouldPoll,
    fetchNextDecisionsPage,
  };
};

export const useTimeFilter = ({
  jobRun,
  isHistorical,
  isMetricsWritten,
}: {
  jobRun: JobRun;
  isHistorical: boolean;
  isMetricsWritten: boolean;
}) => {
  // This way we keep a stable query key by setting 60 minutes from now.
  // Non-stable query key causes react-query to drop existing data and causes flickering.
  // 60 minutes is not ideal but it's a good compromise between data freshness and query key stability.
  const futureTime = useMemo(() => addMinutes(new Date(), 60), []);
  const frozenUntilStartTimeRef = useRef<{
    jobRunId: string;
    untilStartTime: string;
  } | null>(null);

  const untilStartTime = (() => {
    const shouldUseWideWindow =
      !isJobRunCompleted(jobRun) || (!isHistorical && !isMetricsWritten);

    if (
      shouldUseWideWindow ||
      (isJobRunCompleted(jobRun) && isAfter(futureTime, jobRun.updated_at))
    ) {
      return formatISO(futureTime);
    }

    if (frozenUntilStartTimeRef.current?.jobRunId !== jobRun.id) {
      frozenUntilStartTimeRef.current = {
        jobRunId: jobRun.id,
        untilStartTime: formatISO(addMinutes(jobRun.updated_at, 1)),
      };
    }

    return frozenUntilStartTimeRef.current.untilStartTime;
  })();

  return useMemo(
    () => ({
      fromStartTime: formatISO(subMinutes(jobRun.created_at, 1)),
      untilStartTime,
    }),
    [jobRun.created_at, untilStartTime],
  );
};
